import { useQueryClient } from '@tanstack/react-query';
import { T, useTranslate } from '@tolgee/react';
import { useFlag } from '@unleash/proxy-client-react';
import { Divider, message, Space, Upload } from 'antd';
import { MessageInstance } from 'antd/lib/message';
import { RcFile } from 'antd/lib/upload';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

import { ArrowDownSvg, ArrowLeftSvg, QuestionMarkSvg } from '../../../../assets/icons';
import CuripodAIStarsSvg from '../../../../assets/icons/CuripodAIOrangeFilledSvg';
import CuripodAISolidStarsSvg from '../../../../assets/icons/CuripodAISolidStarsSvg';
import { MagicBackground } from '../../../../assets/icons/MagicBackground';
import { ShuffleSvg } from '../../../../assets/icons/ShuffleSvg';
import theme from '../../../../assets/theme';
import GameAPI from '../../../../frontendServices/gameAPI';
import useAccessBadge from '../../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useDynamicLessonStateContext } from '../../../../hooks/useCreateLessonState';
import { buildInitialLessonPromptVariablesQueryKey } from '../../../../hooks/useDesignerUIState';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useLocale } from '../../../../hooks/useLocale';
import { useMetrics } from '../../../../hooks/useMetrics';
import { useNewPresentationId } from '../../../../hooks/useNewPresentationId';
import { usePrevious } from '../../../../hooks/usePrevious';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import { useTestPrepState } from '../../../../hooks/useTestPrepState';
import {
  MaterialAttachment,
  useUploadMaterialAttachment,
} from '../../../../hooks/useUploadMaterialAttachment';
import { useMessage } from '../../../../providers/MessageProvider/MessageProvider';
import SocketProvider, {
  SocketContext,
} from '../../../../providers/SocketProvider/SocketProvider';
import { LLMMessageDTO } from '../../../../services/backendService/backendService';
import {
  FileUploads,
  ProcessDocumentErrorMessage,
} from '../../../../services/backendService/types';
import { lessonCategories, writingSkills } from '../../../../utils/createLessonPageUtils';
import { createFileFromText } from '../../../../utils/fileUtils';
import {
  isFileMimetypeSupported,
  isPresentationMimetype,
  SupportedMimeTypes,
} from '../../../../utils/mimetype';
import { Loader } from '../../../_atoms/_Loader';
import { TextArea } from '../../../_atoms/_TextArea';
import { Button } from '../../../_atoms/Button';
import { CustomIntercomButton } from '../../../_atoms/CustomIntercomButton';
import { HideNextJsLoader } from '../../../_atoms/HideNextJsLoader';
import { IconWrapper } from '../../../_atoms/IconWrapper';
import { LoadingDots } from '../../../_atoms/StreamingLongAnswerList/StreamingLongAnswerList';
import { Slide } from '../../../SlideComponents/slide.types';
import { CenteredContainer } from '../../../styled/CenteredContainer';
import { CategoryList } from '../CategorySelector';
import { SubPageLayout } from '../CreatePage';
import { CurifyTab } from '../CurifyTab';
import { FindCategorizedLessonsTab } from '../FindCategorizedLessonsTab';
import { useLearningObjectiveRefinement } from '../hooks/useLearningObjectiveRefinementHook';
import { MaterialAttachments } from '../MaterialAttachments';
import { TestPrepTab } from '../TestPrepTab';
import { UseMyTextModal } from '../UseMyTextModal';
import { ChatHistoryList } from './ChatHistoryList';
import { ChooseTheme } from './ChooseTheme';
import { MaterialsMenu } from './MaterialsMenu';
import { LessonSuggestion, useLessonSuggestions } from './use-lesson-suggestions';

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const PreventPullToRefresh = () => {
  return (
    <style jsx global>{`
      /* prevent pull-to-refresh for Safari 16+ */
      @media screen and (pointer: coarse) {
        @supports (-webkit-backdrop-filter: blur(1px)) and (overscroll-behavior-y: none) {
          html {
            min-height: 100.3%;
            overscroll-behavior-y: none;
          }
        }
      }
      /* prevent pull-to-refresh for Safari 9~15 */
      @media screen and (pointer: coarse) {
        @supports (-webkit-backdrop-filter: blur(1px)) and
          (not (overscroll-behavior-y: none)) {
          html {
            height: 100%;
            overflow: hidden;
          }
          body {
            margin: 0px;
            max-height: 100%; /* or if body has default margin */
            overflow: auto;
            -webkit-overflow-scrolling: touch;
          }
          /* in this case to disable pinch-zoom, set  */
        }
      }

      /* prevent pull-to-refresh for Chrome 63+ */
      body {
        overscroll-behavior-y: none;
      }
    `}</style>
  );
};
// Mobile Components
const MobileInputContainer = styled.div<{
  $isObjectiveReady: boolean;
  $hasChatHistory: boolean;
  $isTextAreaFocused: boolean;
  $isHovered: boolean;
  $isDisabled: boolean;
}>`
  width: 100%;
  margin: 0 auto;
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? theme.colors.greyLight : theme.colors.white};
  box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.17), 0px 0.3px 30px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: text;
  transition: all 0.5s ease;
  
  ${({ $hasChatHistory }) =>
    $hasChatHistory
      ? `
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    `
      : `

    `}

  ${({ $isHovered }) =>
    $isHovered &&
    `
    border: 1px solid ${theme.colors.greyLight};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
  ${({ $isTextAreaFocused }) =>
    $isTextAreaFocused &&
    `
    border: 1px solid ${theme.colors.grey};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
    ${({ $isObjectiveReady }) =>
      $isObjectiveReady &&
      `
    display: none;
  `}
  
`;

const MobileButtonsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  justify-content: space-between;
  align-items: flex-end;

  @media (max-width: 768px) {
    justify-content: space-between;
    align-items: center;
    gap: 5px;
  }
`;

const MobileRightButtonsContainer = styled.div`
  display: flex;
  gap: 10px;
  width: fit-content;
  flex-wrap: wrap;
  align-items: center;
  margin-left: auto;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

const MobileSubmitButton = styled(Button)`
  height: 30px;
  width: 30px;
  border-radius: 20px;
  padding: 20px;
  background: ${theme.colors.orangeGradient};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.white} !important;
  gap: 10px;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    background: linear-gradient(45deg, #ffa55b, #ffc107);
    background-size: 400% 400%;
    animation: ${gradientShift} 2s ease infinite;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    background: ${theme.colors.orangeGradient} !important;
    cursor: not-allowed;
  }

  div {
    cursor: inherit;
  }

  svg {
    margin-right: 0px;
  }

  @media (max-width: 768px) {
    height: 35px;
    width: 35px;
    padding: 18px;
    border-radius: 18px;
  }
`;

const MobileLessonReadyButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
  @media (min-width: 769px) {
    display: none;
  }
`;

const LessonReadyButtons = styled.div`
  position: absolute;
  bottom: 0px;
  top: 10px;
  right: 0px;
  width: 100%;
  height: 100px;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

export const GenerateLessonButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background: linear-gradient(45deg, #ffa55b, #ffc107, #ffa55b, #ffc107);
  background-size: 600% 400%;
  animation: ${gradientShift} 6s ease infinite;
  color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    background: linear-gradient(45deg, #ffa55b, #ffc107);
  }

  &:active,
  &:focus {
    background: linear-gradient(45deg, #ffa55b, #ffc107);
  }

  svg {
    height: 25px;
    width: 25px;
  }
`;

const ObjectiveRefinementWithSocket = () => {
  useLocale();
  const dispatchAndSync = useDynamicLessonStateContext(
    context => context.dispatchAndSync,
  );
  const agentHistory = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.agentHistory,
  );
  const { t } = useTranslate();
  const { tenantId, router } = useRouterMatch();
  const [inputValue, setInputValue] = useState('');
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
  const [isInputContainerHovered, setIsInputContainerHovered] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const streamCleanupRef = useRef<(() => void) | null>(null);
  const [showPasteModal, setShowPasteModal] = useState(false);
  const hasProcessedUrlPrompt = useRef(false);
  const metrics = useMetrics();
  const client = useQueryClient();
  const nextRouter = useRouter();
  const isDesktop = useBreakPoint(768);
  const [chatSubmissionCounter, setChatSubmissionCounter] = useState(0);
  const uploadRef = useRef<HTMLButtonElement>(null);
  const mobileUploadRef = useRef<HTMLButtonElement>(null);
  const isStreaming = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isStreaming,
  );
  const wasStreamingOnPreviousRender = usePrevious(isStreaming);
  useEffect(() => {
    if (wasStreamingOnPreviousRender && !isStreaming) {
      inputRef.current?.focus();
    }
  }, [wasStreamingOnPreviousRender, isStreaming]);

  const promptVariables = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.promptVariables,
  );

  const llmThreadId = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.llmThreadId,
  );

  const isObjectiveReady = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isObjectiveReady,
  );
  const isComplete = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isComplete,
  );

  const isFlexibleLessonOneFlagEnabled = useFlag('flexible-lesson-one');

  const { gameAPI } = useContext(SocketContext);

  const [attachments, setAttachments] = useState<Array<MaterialAttachment>>([]);

  // this state is used to store attachments after a prompt is submitted. The Chat interface grabs attachments from LLMMessage.attachments
  // but that data structure isn't sufficiently complex to represent the state of the user having uploaded an image or pdf,
  // and we've created two file uploads from it (the original which the user sees, and the docling markdown file we feed to the LLM).
  // This is not an ideal solution, but we think the chat-based interface is about to change significantly, so this shortcut is likely tech debt that we
  // can default on soon =)
  const [intentRefinementAttachments, setIntentRefinementAttachments] = useState<
    Array<MaterialAttachment>
  >([]);
  const { startStream, stopStream } = useLearningObjectiveRefinement();

  const isConnected = useDynamicLessonStateContext(
    context => context.dynamicLessonState.agent.isConnected,
  );

  const submitPrompt = useCallback(
    (prompt: string, messages: LLMMessageDTO[] = []) => {
      // Add user message to chat history immediately
      const userMessage: LLMMessageDTO = {
        role: 'USER',
        content: prompt,
        createdAt: new Date().toISOString(),
        id: 'unknown',
        model: 'o3',
        modifiedAt: new Date().toISOString(),
        threadId: 'unknown',
        attachments: attachments.map(
          a =>
            (({
              id: a.fileUploadId,
              fileId: a.fileId,
              mimeType: a.mimetype,
              fileName: a.fileName,
            } as unknown) as FileUploads),
        ) as FileUploads[],
      };
      metrics.logEvent('CreatePage.IntentRefinement.InitialPromptSubmission', {
        prompt,
        attachments: attachments.map(a => ({ fileUploadId: a.fileUploadId })),
      });
      dispatchAndSync({
        type: 'SET_AGENT_HISTORY',
        payload: [...(messages || []), userMessage],
      });
      streamCleanupRef.current?.();

      // Start the stream with just the message
      const cleanup = startStream({
        historyLength: messages.length,
        message: prompt,
        attachments: attachments.map(a => ({ fileUploadId: a.fileUploadId })),
      });
      streamCleanupRef.current = cleanup || null;
    },
    [attachments, dispatchAndSync, startStream],
  );

  const handleSubmit = () => {
    if (!inputValue.trim() && attachments.length === 0) return;
    submitPrompt(inputValue, agentHistory);
    setInputValue('');
    if (agentHistory.length === 0) {
      setIntentRefinementAttachments(attachments);
    }
    setAttachments([]);
    setChatSubmissionCounter(prev => prev + 1);
  };

  // if being redirected from landing page, auto-submit the prompt
  useEffect(() => {
    if (
      nextRouter.query.prompt &&
      typeof nextRouter.query.prompt === 'string' &&
      agentHistory.length === 0 &&
      !hasProcessedUrlPrompt.current
    ) {
      hasProcessedUrlPrompt.current = true;
      // Clear the URL parameter immediately to prevent re-triggering
      nextRouter.replace(`/${tenantId}/create`, undefined, { shallow: true });

      submitPrompt(nextRouter.query.prompt);
      setChatSubmissionCounter(prev => prev + 1);
    }
  }, [submitPrompt, nextRouter, tenantId, agentHistory]);

  const {
    createPresentationId,
    isLoading: isCreatingPresentationId,
  } = useNewPresentationId();

  const { suggestions, shuffleSuggestions } = useLessonSuggestions();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, []);

  const handleSuggestionClick = (fullValue: string) => {
    setInputValue(fullValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleGenerateLesson = () => {
    if (!promptVariables) {
      return;
    }

    createPresentationId({
      onSuccess: presentationId => {
        client.setQueryData(
          buildInitialLessonPromptVariablesQueryKey(presentationId),
          promptVariables,
        );

        // Moved it here to we can capture the presentationId
        metrics.logEvent('CreatePage.ObjectiveRefinement.GenerateLessonClicked', {
          chatHistoryLength: agentHistory.length,
          sessionLength: 0,
          isObjectiveReady,
          ...promptVariables,
          tenantId,
          attachments: (promptVariables.attachments ?? []).map(a => ({
            fileUploadId: a.fileUploadId,
          })),
          presentationId,
          llmThreadId: llmThreadId,
        });

        if (isFlexibleLessonOneFlagEnabled && gameAPI) {
          // move to theme picker tab and start lesson generation under the hood
          gameAPI.getSocket().emit(
            'build:lesson',
            {
              presentationId,
              message: 'Generate lesson',
              ...promptVariables, //
            },
            (_: {
              status: number;
              data: { slides: Slide[]; messages: LLMMessageDTO[] };
              error?: { status: number; message: string };
            }) => {
              dispatchAndSync({
                type: 'SET_COLOR_PALETTE_PICKER',
                payload: { presentationId },
              });
            },
          );
        } else {
          router.push(
            `/${tenantId}/lessons/${presentationId}/edit?buildInitialLesson=true`,
          );
        }
      },
    });
  };

  const [isUploadingFileText, setIsUploadingFileText] = useState(false);
  const {
    upload: uploadFile,
    isUploading: isUploadingFile,
  } = useUploadMaterialAttachment(tenantId);

  const isUploading = isUploadingFile || isUploadingFileText;

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const text = e.clipboardData.getData('text/plain');
    e.preventDefault();

    // If chat has started, always insert inline (no attachments)
    const shouldInsertInline = agentHistory.length >= 1 || true; //text.length < 100;
    if (shouldInsertInline) {
      // Insert text at cursor position instead of replacing entire value
      const textArea = e.target as HTMLTextAreaElement;
      const start = textArea.selectionStart;
      const end = textArea.selectionEnd;
      const currentValue = textArea.value;
      const newValue =
        currentValue.substring(0, start) + text + currentValue.substring(end);

      setInputValue(newValue);
      return;
    }
    // Otherwise we make a txt file.

    // Create a .txt file from the text
    const file = createFileFromText(text);
    uploadFile(
      { file, attachmentMethod: 'paste' },
      {
        onSuccess: result => {
          setAttachments(prev => [...prev, result.materialAttachment]);
        },
        onError: () => {
          // TODO: display to user
        },
      },
    );
  };
  const isSubmitPromptDisabled =
    (!inputValue.trim() && attachments.length === 0) ||
    isStreaming ||
    !isConnected ||
    isUploading;

  const messageApi = useMessage();

  if (!isDesktop) {
    return (
      <ContainerMobile $isChatHistory={agentHistory.length >= 1}>
        <PreventPullToRefresh />

        <Title $hasHistory={agentHistory.length >= 1}>
          <T keyName="IntentRefinement.Title" defaultValue="Lessons Students Love" />
        </Title>
        <Subtitle $hasHistory={agentHistory.length >= 1}>
          <T
            keyName="IntentRefinement.Subtitle"
            defaultValue="Create with AI by typing your topic, standard, or learning objective"
          />
        </Subtitle>

        {agentHistory.length >= 1 && (
          <ChatHistoryList
            onChatSubmitted={chatSubmissionCounter}
            attachments={intentRefinementAttachments}
          />
        )}

        <MobileFormContainer
          $hasChatHistory={agentHistory.length >= 1}
          $isObjectiveReady={isObjectiveReady}
        >
          <MobileInputContainer
            $isObjectiveReady={isObjectiveReady}
            $isDisabled={isStreaming}
            $hasChatHistory={agentHistory.length >= 1}
            $isTextAreaFocused={isTextAreaFocused}
            $isHovered={isInputContainerHovered}
            onMouseEnter={() => setIsInputContainerHovered(true)}
            onMouseLeave={() => setIsInputContainerHovered(false)}
            onClick={() => {
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                justifyContent: 'space-between',
                padding: '10px 10px 6px 10px',
              }}
            >
              <TextArea
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onFocus={() => setIsTextAreaFocused(true)}
                onBlur={() => setIsTextAreaFocused(false)}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                fluid={true}
                placeholder={
                  !isConnected && agentHistory.length > 0
                    ? t('Connecting to server...', 'Connecting to server...')
                    : agentHistory.length === 0 && !isStreaming
                    ? t(
                        'Describe your lesson: For example: Analyzing theme in The Giver, 8th grade, 30 minutes',
                        'Describe your lesson: For example: Analyzing theme in The Giver, 8th grade, 30 minutes',
                      )
                    : isStreaming || isObjectiveReady
                    ? '' // Empty when streaming to show animated overlay
                    : t('Reply to Curipod AI...', 'Reply to Curipod AI...')
                }
                autoSize={{
                  minRows: agentHistory.length > 0 ? 1 : 3,
                  maxRows: agentHistory.length > 0 ? 4 : 8,
                }}
                ref={inputRef}
                autoFocus
                disabled={isStreaming || isObjectiveReady}
                textAreaStyled={{
                  fontSize: 16,
                  fontWeight: 600,
                  padding: '15px',
                  borderRadius: 15,
                  border: 'none',
                  backgroundColor: 'transparent',
                }}
                style={{
                  width: '100%',
                }}
              />

              {isStreaming && (
                <MobileLoadingOverlay>
                  <T keyName="Generating" defaultValue="Generating" />
                  <LoadingDots>...</LoadingDots>
                </MobileLoadingOverlay>
              )}

              {!agentHistory.length && (
                <MobileButtonsBar>
                  <MaterialsMenu
                    onClickUploadFile={() => mobileUploadRef.current?.click()}
                    tenantId={tenantId}
                    onFileUpload={file => {
                      setAttachments(prev => [...prev, file]);
                      setIsUploadingFileText(false);
                    }}
                    eventMetadata={{
                      promptInput: inputValue,
                    }}
                    onSelectCurifyMySlides={() =>
                      dispatchAndSync({
                        type: 'SET_MAIN_LESSON_CATEGORY',
                        payload: 'upload',
                      })
                    }
                  />

                  <MobileRightButtonsContainer onClick={e => e.stopPropagation()}>
                    <MobileSubmitButton
                      onClick={e => {
                        e.stopPropagation();
                        handleSubmit();
                      }}
                      disabled={isSubmitPromptDisabled}
                    >
                      <IconWrapper
                        style={{ transform: 'rotate(-90deg)' }}
                        height="20px"
                        width="20px"
                        color={theme.colors.white}
                      >
                        <ArrowDownSvg />
                      </IconWrapper>
                    </MobileSubmitButton>
                  </MobileRightButtonsContainer>
                </MobileButtonsBar>
              )}

              {(isUploading || attachments.length > 0) && (
                <MaterialAttachments
                  isUploading={isUploading}
                  attachments={attachments}
                  removeAttachment={(fileId: string) => {
                    setAttachments(prev => prev.filter(a => a.fileId !== fileId));
                  }}
                />
              )}
              {agentHistory.length >= 1 && !isStreaming && !isObjectiveReady && (
                <MobileSubmitButtonWrapper>
                  <MobileSubmitButton
                    onClick={handleSubmit}
                    disabled={!inputValue.trim() || isStreaming || !isConnected}
                  >
                    <IconWrapper
                      style={{ transform: 'rotate(180deg)' }}
                      height="20px"
                      width="20px"
                      color={theme.colors.white}
                    >
                      <ArrowDownSvg />
                    </IconWrapper>
                  </MobileSubmitButton>
                </MobileSubmitButtonWrapper>
              )}
              {isStreaming && (
                <MobileSubmitButtonWrapper>
                  <MobileSubmitButton
                    onClick={() => stopStream()}
                    styles={{
                      background: theme.colors.greyLight,
                      border: `1px solid ${theme.colors.grey}`,
                    }}
                  >
                    <IconWrapper height="20px" width="20px" color={theme.colors.greyDark}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 14 14"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6 0H1.4C0.6286 0 0 0.6286 0 1.4V12.6C0 13.3714 0.6286 14 1.4 14H12.6C13.3714 14 14 13.3714 14 12.6V1.4C14 0.6286 13.3714 0 12.6 0Z"
                          fill="currentColor"
                        />
                      </svg>
                    </IconWrapper>
                  </MobileSubmitButton>
                </MobileSubmitButtonWrapper>
              )}
            </div>
            {!(isUploading || attachments.length > 0) && !agentHistory.length && (
              <PromptSuggestions
                onOptionClick={handleSuggestionClick}
                suggestions={suggestions}
                shuffleSuggestions={shuffleSuggestions}
              />
            )}
          </MobileInputContainer>

          {/* Mobile lesson ready buttons - fixed position */}
          {isObjectiveReady && agentHistory.length > 0 && !isStreaming && isComplete && (
            <MobileLessonReadyButtons>
              <Button
                type="ghost"
                onClick={() => {
                  // Track when user clicks "Keep chatting" instead of generating lesson
                  metrics.logEvent('CreatePage.ObjectiveRefinement.KeepChattingClicked', {
                    chatHistoryLength: agentHistory.length,
                    sessionLength: 0,
                    isObjectiveReady: isObjectiveReady,
                    promptVariables: promptVariables
                      ? {
                          learningObjective: promptVariables.learningObjective,
                          grade: promptVariables.grade,
                          subject: promptVariables.subject,
                          languages: promptVariables.languages,
                          lessonCategory: promptVariables.lessonCategory,
                          standardAlignment: promptVariables.standardAlignment,
                        }
                      : null,
                  });

                  // Focus back on the input to continue chatting
                  if (inputRef.current) {
                    dispatchAndSync({
                      type: 'SET_AGENT_KEEP_EDITING',
                    });
                    setTimeout(() => {
                      inputRef.current?.focus();
                    }, 100);
                  }
                }}
                styles={{
                  width: '100%',
                  height: '50px',
                  borderRadius: '25px',
                  backgroundColor: theme.colors.white,
                  border: `1px solid ${theme.colors.grey}`,
                }}
              >
                <T keyName="Keep chatting" defaultValue="Keep chatting" />
              </Button>
              <GenerateLessonButton
                onClick={handleGenerateLesson}
                loading={isCreatingPresentationId}
                icon={
                  <IconWrapper height="20px" width="20px">
                    <CuripodAISolidStarsSvg />
                  </IconWrapper>
                }
                styles={{
                  width: '100%',
                  height: '50px',
                  borderRadius: '25px',
                }}
              >
                <T keyName="Generate lesson" defaultValue="Generate lesson" />
              </GenerateLessonButton>
            </MobileLessonReadyButtons>
          )}
        </MobileFormContainer>

        <UseMyTextModal
          tenantId={tenantId}
          isVisible={showPasteModal}
          onCancel={() => setShowPasteModal(false)}
          onFileUploadSuccess={file => {
            setAttachments(prev => [...prev, file]);
          }}
        />
        <Upload
          style={{ display: 'none' }}
          accept={SupportedMimeTypes.join(',')}
          showUploadList={false}
          customRequest={({ file }: { file: string | Blob | RcFile }) => {
            uploadFile(
              { file: file as File, attachmentMethod: 'upload' },
              {
                onSuccess: result => {
                  setAttachments(prev => [...prev, result.materialAttachment]);
                },
                onError: error => {
                  handleUploadError(
                    ((error as Error)?.message as ProcessDocumentErrorMessage) ??
                      'Unknown failure reason',
                    messageApi,
                  );
                },
              },
            );
          }}
        >
          <button style={{ display: 'none' }} ref={mobileUploadRef} />
        </Upload>
      </ContainerMobile>
    );
  }

  // Desktop version
  return (
    <Container $isChatHistory={agentHistory.length >= 1}>
      <Title $hasHistory={agentHistory.length >= 1}>
        <T
          keyName="IntentRefinement.Title.Desktop"
          defaultValue="Lessons Students Love"
        />
      </Title>
      <Subtitle $hasHistory={agentHistory.length >= 1}>
        <T
          keyName="IntentRefinement.Subtitle"
          defaultValue="Create with AI by typing your topic, standard, or learning objective"
        />
      </Subtitle>

      <ChatHistoryList
        onChatSubmitted={chatSubmissionCounter}
        attachments={intentRefinementAttachments}
      />

      <FormContainer
        style={{
          maxHeight: agentHistory.length >= 1 ? 'fit-content' : 'unset',
          paddingBottom: agentHistory.length >= 1 ? '10px' : '20px',
        }}
      >
        <InputContainer
          $isDisabled={isStreaming}
          $hasChatHistory={agentHistory.length >= 1}
          $isTextAreaFocused={isTextAreaFocused}
          $isHovered={isInputContainerHovered}
          onMouseEnter={() => setIsInputContainerHovered(true)}
          onMouseLeave={() => setIsInputContainerHovered(false)}
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          <Upload.Dragger
            listType="text"
            style={{
              width: '100%',
              backgroundColor: 'transparent',
              border: 'none',
            }}
            accept={SupportedMimeTypes.join(',')}
            openFileDialogOnClick={false}
            type="drag"
            showUploadList={false}
            onDrop={event => {
              const text = event.dataTransfer.getData('text/plain');
              const filesToUpload = Array.from(event.dataTransfer.files);
              if (filesToUpload.length > 1) {
                message.error(
                  t(
                    'UseMyMaterials.Document.SelectFile.TooManyFilesError',
                    'Only one file can be uploaded at a time',
                  ),
                );
                return;
              }
              // this happens if someone drags selected text from another window
              if (filesToUpload.length === 0 && text) {
                uploadFile(
                  { file: createFileFromText(text), attachmentMethod: 'paste' },
                  {
                    onSuccess: result => {
                      setAttachments(prev => [...prev, result.materialAttachment]);
                    },
                  },
                );
                return;
              }
              const file = filesToUpload[0];
              if (!file) {
                message.error(
                  t(
                    'UseMyMaterials.Document.SelectFile.NoFileSelectedError',
                    'No file selected',
                  ),
                );
                return;
              }
              const mimetype = file.type;
              if (!isFileMimetypeSupported(mimetype)) {
                if (isPresentationMimetype(mimetype)) {
                  message.error(
                    t(
                      'UseMyMaterials.Document.SelectFile.PresentationFileError',
                      "Try 'Upload Slides' instead",
                    ),
                  );
                } else if (mimetype.startsWith('image/')) {
                  message.error(
                    t(
                      'UseMyMaterials.Document.SelectFile.UnsupportedImageFileTypeError',
                      'Unsupported file type. Try uploading a jpg, png, or webp file',
                    ),
                  );
                } else if (mimetype.startsWith('application/')) {
                  message.error(
                    t(
                      'UseMyMaterials.Document.SelectFile.UnsupportedApplicationFileTypeError',
                      'Unsupported file type. Try uploading a pdf or docx file',
                    ),
                  );
                } else if (mimetype.startsWith('audio/')) {
                  message.error(
                    t(
                      'UseMyMaterials.Document.SelectFile.UnsupportedAudioFileTypeError',
                      "Unsupported file type. We don't support audio files yet!",
                    ),
                  );
                } else if (mimetype.startsWith('video/')) {
                  message.error(
                    t(
                      'UseMyMaterials.Document.SelectFile.UnsupportedVideoFileTypeError',
                      "Unsupported file type. We don't support video files yet!",
                    ),
                  );
                } else {
                  message.error(
                    t(
                      'UseMyMaterials.Document.SelectFile.UnsupportedFileTypeError',
                      'Unsupported file type',
                    ),
                  );
                }
              }
            }}
            customRequest={({ file }: { file: string | Blob | RcFile }) => {
              uploadFile(
                { file: file as File, attachmentMethod: 'upload' },
                {
                  onSuccess: result => {
                    setAttachments(prev => [...prev, result.materialAttachment]);
                  },
                  onError: error => {
                    handleUploadError(
                      ((error as Error)?.message as ProcessDocumentErrorMessage) ??
                        'Unknown failure reason',
                      messageApi,
                    );
                  },
                },
              );
            }}
          >
            <div style={{ padding: '10px 10px 6px 10px' }}>
              <TextArea
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onFocus={() => setIsTextAreaFocused(true)}
                onBlur={() => setIsTextAreaFocused(false)}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                placeholder={
                  !isConnected && agentHistory.length > 0
                    ? t('Connecting to server...', 'Connecting to server...')
                    : agentHistory.length === 0 && !isStreaming
                    ? t(
                        'Chat.Prompt.DescribeYourLesson',
                        'Describe your lesson: For example: Analyzing theme in The Giver, 8th grade, 30 minutes',
                      )
                    : isStreaming || isObjectiveReady
                    ? '' // Empty when streaming to show animated overlay
                    : t('Reply to Curipod AI...', 'Reply to Curipod AI...')
                }
                autoSize={{ minRows: 2, maxRows: 10 }}
                ref={inputRef}
                autoFocus
                disabled={isStreaming || isObjectiveReady}
                textAreaStyled={{
                  fontSize: 18,
                  fontWeight: 600,
                  padding: '10px',
                  borderRadius: 0,
                  border: 'none',
                  backgroundColor: 'transparent',
                }}
                style={{
                  minWidth: '100%',
                }}
              />
              {isStreaming && (
                <LoadingOverlay>
                  <T keyName="Generating" defaultValue="Generating" />
                  <LoadingDots>...</LoadingDots>
                </LoadingOverlay>
              )}

              {!agentHistory.length && (
                <ButtonsBar>
                  <MaterialsMenu
                    onClickUploadFile={() => uploadRef.current?.click()}
                    tenantId={tenantId}
                    onFileUpload={file => {
                      setAttachments(prev => [...prev, file]);
                      setIsUploadingFileText(false);
                    }}
                    eventMetadata={{
                      promptInput: inputValue,
                    }}
                    onSelectCurifyMySlides={() =>
                      dispatchAndSync({
                        type: 'SET_MAIN_LESSON_CATEGORY',
                        payload: 'upload',
                      })
                    }
                  />
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      width: 'fit-content',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      marginLeft: 'auto',
                    }}
                  >
                    <SubmitButton
                      onClick={handleSubmit}
                      disabled={isSubmitPromptDisabled}
                    >
                      <IconWrapper
                        style={{ transform: 'rotate(-90deg)' }}
                        height="20px"
                        width="20px"
                        color={theme.colors.white}
                      >
                        <ArrowDownSvg />
                      </IconWrapper>
                    </SubmitButton>
                  </div>
                </ButtonsBar>
              )}

              {(isUploading || attachments.length > 0) && (
                <MaterialAttachments
                  isUploading={isUploading}
                  attachments={attachments}
                  removeAttachment={(fileId: string) => {
                    setAttachments(prev => prev.filter(a => a.fileId !== fileId));
                  }}
                />
              )}
              {agentHistory.length >= 1 && !isStreaming && (
                <SubmitButton
                  onClick={handleSubmit}
                  disabled={!inputValue.trim() || isStreaming || !isConnected}
                  styles={{
                    marginLeft: 'auto',
                    visibility: isObjectiveReady ? 'hidden' : 'visible',
                  }}
                >
                  <IconWrapper
                    style={{ transform: 'rotate(180deg)' }}
                    height="20px"
                    width="20px"
                    color={theme.colors.white}
                  >
                    <ArrowDownSvg />
                  </IconWrapper>
                </SubmitButton>
              )}
              {isStreaming && (
                <Button
                  type="ghost"
                  onClick={() => stopStream()}
                  icon={
                    <IconWrapper height="14px" width="14px" color={theme.colors.greyDark}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 14 14"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6 0H1.4C0.6286 0 0 0.6286 0 1.4V12.6C0 13.3714 0.6286 14 1.4 14H12.6C13.3714 14 14 13.3714 14 12.6V1.4C14 0.6286 13.3714 0 12.6 0Z"
                          fill="currentColor"
                        />
                      </svg>
                    </IconWrapper>
                  }
                  styles={{
                    marginLeft: 'auto',
                    width: 'fit-content',
                    borderRadius: '20px',
                    height: '40px',
                  }}
                >
                  <T keyName="Stop" defaultValue="Stop" />
                </Button>
              )}
              {/* Show Generate Lesson options when objective is ready */}
              {isObjectiveReady && agentHistory.length > 0 && !isStreaming && isComplete && (
                <LessonReadyButtons>
                  <Button
                    type="ghost"
                    onClick={() => {
                      // Track when user clicks "Keep chatting" instead of generating lesson
                      metrics.logEvent(
                        'CreatePage.ObjectiveRefinement.KeepChattingClicked',
                        {
                          chatHistoryLength: agentHistory.length,
                          sessionLength: 0,
                          isObjectiveReady: isObjectiveReady,
                          promptVariables: promptVariables
                            ? {
                                learningObjective: promptVariables.learningObjective,
                                grade: promptVariables.grade,
                                subject: promptVariables.subject,
                                languages: promptVariables.languages,
                                lessonCategory: promptVariables.lessonCategory,
                                standardAlignment: promptVariables.standardAlignment,
                              }
                            : null,
                        },
                      );

                      // Focus back on the input to continue chatting
                      if (inputRef.current) {
                        dispatchAndSync({
                          type: 'SET_AGENT_KEEP_EDITING',
                        });
                        // :grimacing_emoji:
                        setTimeout(() => {
                          inputRef.current?.focus();
                        }, 100);
                      }
                    }}
                  >
                    <T keyName="Keep chatting" defaultValue="Keep chatting" />
                  </Button>
                  <GenerateLessonButton
                    onClick={handleGenerateLesson}
                    loading={isCreatingPresentationId}
                    icon={
                      <IconWrapper height="20px" width="20px">
                        <CuripodAISolidStarsSvg />
                      </IconWrapper>
                    }
                  >
                    <T keyName="Generate lesson" defaultValue="Generate lesson" />
                  </GenerateLessonButton>
                </LessonReadyButtons>
              )}
            </div>
            {!(isUploading || attachments.length > 0) && !agentHistory.length && (
              <PromptSuggestions
                onOptionClick={handleSuggestionClick}
                suggestions={suggestions}
                shuffleSuggestions={shuffleSuggestions}
              />
            )}
          </Upload.Dragger>
        </InputContainer>
      </FormContainer>
      <Upload
        style={{ display: 'none' }}
        accept={SupportedMimeTypes.join(',')}
        showUploadList={false}
        customRequest={({ file }: { file: string | Blob | RcFile }) => {
          uploadFile(
            { file: file as File, attachmentMethod: 'upload' },
            {
              onSuccess: result => {
                setAttachments(prev => [...prev, result.materialAttachment]);
              },
              onError: error => {
                handleUploadError(
                  ((error as Error)?.message as ProcessDocumentErrorMessage) ??
                    'Unknown failure reason',
                  messageApi,
                );
              },
            },
          );
        }}
      >
        <button style={{ display: 'none' }} ref={uploadRef} />
      </Upload>
    </Container>
  );
};

function PromptSuggestions({
  onOptionClick,
  suggestions,
  shuffleSuggestions,
}: {
  suggestions: LessonSuggestion[];
  onOptionClick: (option: string) => void;
  shuffleSuggestions: () => void;
}) {
  const { t } = useTranslate();

  return (
    <>
      <Divider style={{ margin: '0px' }} />
      <div
        style={{
          background: '#FFF9F3',
          borderRadius: '0px 0px 10px 10px',
          display: 'flex',
          justifyContent: 'space-between',
          padding: '5px 10px',
          width: '100%',
          maxWidth: '800px',
        }}
      >
        <Space size="large">
          {suggestions.map(suggestion => (
            <Button
              key={suggestion.promptKey}
              type="linkStyle"
              onClick={() => onOptionClick(suggestion.promptDefault)}
              styles={{
                fontSize: '12px',
                fontWeight: 'bold',
                color: theme.colors.greyDark,
              }}
            >
              {suggestion.icon}
              {suggestion.summary}
            </Button>
          ))}
        </Space>
        <ShuffleButton
          onClick={shuffleSuggestions}
          title={t('Get new suggestions', 'Get new suggestions')}
        >
          <IconWrapper height="16px" width="16px" color={theme.colors.greyDark}>
            <ShuffleSvg />
          </IconWrapper>
        </ShuffleButton>
      </div>
    </>
  );
}

const messageDuration = 5;
function handleUploadError(
  errorMessage: ProcessDocumentErrorMessage,
  messageApi: MessageInstance,
) {
  switch (errorMessage) {
    case 'Failed to find content in parsed file':
      return messageApi.error(
        <T
          keyName="Materials.FailedToFindContent"
          defaultValue="Failed to find content in file. For best results, ensure your file contains clear, legible text."
        />,
        messageDuration,
      );
    case 'Failed to process document':
      return messageApi.error(
        <T
          keyName="Materials.FailedToProcessDocument"
          defaultValue="We couldn't process that file. It may be too large or corrupted. Please try again with a different file."
        />,
        messageDuration,
      );
    case 'Unknown failure reason':
      return messageApi.error(
        <T
          keyName="Materials.UnknownFailureReason"
          defaultValue="Something went wrong. Please try again or reach out in the chat for help!"
        />,
        messageDuration,
      );
    default:
      try {
        assertNever(errorMessage);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`Unexpected error message: ${errorMessage}`);
        return messageApi.error(
          <T
            keyName="Materials.UnknownFailureReason"
            defaultValue="Something went wrong. Please try again or reach out in the chat for help!"
          />,
          messageDuration,
        );
      }
  }
}

function assertNever(value: never): never {
  throw new Error(`Unexpected value: ${value}`);
}

const Container = styled.div<{ $isChatHistory: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: ${({ $isChatHistory }) => ($isChatHistory ? '0px' : '20vh')};
  height: 100vh;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  transition: all 0.8s ease-in-out;
  position: relative;
  z-index: 2;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 20px;
    background: #fbf7f2;
    background: linear-gradient(
      0deg,
      rgba(251, 247, 242, 0) 0%,
      rgba(251, 247, 242, 1) 100%
    );
  }
`;

const ContainerMobile = styled.div<{ $isChatHistory: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: ${({ $isChatHistory }) => ($isChatHistory ? '30px' : '20vh')};
  min-height: 100dvh;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  transition: all 0.8s ease-in-out;
  position: relative;
  z-index: 2;

  //ChatHistory scroll fix
  ${({ $isChatHistory }) =>
    $isChatHistory &&
    `
    overflow: hidden;
    height: 100dvh;
    touch-action: none;
    position: fixed;
  `}

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    background: #fbf7f2;
    background: linear-gradient(
      0deg,
      rgba(251, 247, 242, 0) 0%,
      rgba(251, 247, 242, 1) 100%
    );
  }
`;

const Title = styled.h1<{ $hasHistory: boolean }>`
  font-size: 48px;
  font-weight: 600;
  color: ${theme.colors.black};
  margin-bottom: 8px;
  opacity: ${({ $hasHistory }) => ($hasHistory ? '0' : '1')};
  margin-top: ${({ $hasHistory }) =>
    $hasHistory
      ? // margin stacks! so if the top of chat goes missing, it means there's too much negative margin =)
        '-50px'
      : '0px'};
  transition: all 0.8s ease-in-out;

  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 5px;
    padding: 0 20px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 28px;
    margin-bottom: 5px;
  }
`;

const Subtitle = styled.h3<{ $hasHistory: boolean }>`
  font-size: 20px;
  font-weight: 600;
  color: ${theme.colors.black};
  opacity: ${({ $hasHistory }) => ($hasHistory ? '0' : '1')};
  // margin stacks! so if the top of chat goes missing, it means there's too much negative margin =)
  margin-top: ${({ $hasHistory }) => ($hasHistory ? '-50px' : '0px')};
  margin-bottom: 0px;
  transition: all 0.8s ease-in-out;

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 0 20px;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const InputContainer = styled.div<{
  $hasChatHistory: boolean;
  $isTextAreaFocused: boolean;
  $isHovered: boolean;
  $isDisabled: boolean;
}>`
  width: 100%;
  max-width: 800px;
  margin-bottom: ${({ $hasChatHistory }) => ($hasChatHistory ? '0px' : '0px')};
  position: relative;
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? theme.colors.greyLight : theme.colors.white};
  box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.17), 0px 0.3px 30px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  /* padding: 10px; */
  border: 1px solid transparent;
  cursor: text;
  transition: all 0.5s ease;

  ${({ $isHovered }) =>
    $isHovered &&
    `
    border: 1px solid ${theme.colors.greyLight};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
  ${({ $isTextAreaFocused }) =>
    $isTextAreaFocused &&
    `
    border: 1px solid ${theme.colors.grey};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
`;

const FormContainer = styled.div`
  width: 100%;
  padding: 0px 20px 20px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 0px 10px 20px 10px;
  }
`;

const MobileFormContainer = styled.div<{
  $hasChatHistory: boolean;
  $isObjectiveReady: boolean;
}>`
  width: 100%;
  padding: 20px 20px 20px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;

  ${({ $hasChatHistory }) =>
    $hasChatHistory
      ? `
    position: sticky;
    left: 0;
    right: 0;
    padding: 10px 20px;
    background-color: transparent;

    `
      : `
    position: relative;
    `}

  ${({ $isObjectiveReady }) =>
    $isObjectiveReady &&
    `
    background-color: ${theme.colors.white};
    box-shadow: 0px -1.6px 3.6px rgba(0, 0, 0, 0.17);
  `}
`;

const MobileSubmitButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  align-self: flex-end;
  margin-top: auto;
`;

const SubmitButton = styled(Button)`
  width: fit-content;
  height: 30px;
  width: 30px;
  border-radius: 20px;
  padding: 20px;
  background: ${theme.colors.orangeGradient};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.white} !important;
  gap: 10px;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    background: linear-gradient(45deg, #ffa55b, #ffc107);
    background-size: 400% 400%;
    animation: ${gradientShift} 2s ease infinite;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    background: ${theme.colors.orangeGradient} !important;
    cursor: not-allowed;
  }

  div {
    cursor: inherit;
  }

  svg {
    margin-right: 0px;
  }
`;

const ShuffleButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(251, 247, 242, 0.8);
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.paleBeige};
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ButtonsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  justify-content: space-between;
  align-items: flex-end;
`;

// Loading Overlay Component
const LoadingOverlay = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  font-size: 18px;
  font-weight: 600;
  pointer-events: none;
  color: ${theme.colors.greyDark};
`;

// Mobile Loading Overlay Component
const MobileLoadingOverlay = styled.div`
  position: absolute;
  left: 0;
  transform: translate(50%, 50%);
  font-size: 16px;
  font-weight: 600;
  pointer-events: none;
  color: ${theme.colors.greyDark};
  text-align: center;
  z-index: 10;
`;

const ObjectiveRefinementComponent = () => {
  const { t } = useTranslate();
  const session = useSession();
  const { tenantId } = useRouterMatch();
  const metrics = useMetrics();
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const isAgentHistoryEmpty = useDynamicLessonStateContext(
    c => c.dynamicLessonState.agent.agentHistory.length === 0,
  );
  const mainLessonCategory = useDynamicLessonStateContext(
    c => c.dynamicLessonState.mainLessonCategory,
  );
  const grade = useDynamicLessonStateContext(c => c.dynamicLessonState.grade);

  const generatorFilterCategory = useDynamicLessonStateContext(
    c => c.dynamicLessonState.generatorFilterCategory,
  );
  const prompt = useDynamicLessonStateContext(c => c.dynamicLessonState.prompt);
  const uploadedFile = useDynamicLessonStateContext(
    c => c.dynamicLessonState.uploadedFile,
  );
  const lessonIntent = useDynamicLessonStateContext(
    c => c.dynamicLessonState.lessonIntent,
  );
  const subject = useDynamicLessonStateContext(c => c.dynamicLessonState.subject);
  const currentPage = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedPage,
  );
  const isDesktop = useBreakPoint(700);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const isTestPrepTemplatesEnabled = useFeatureFlag('templated_customizable_lessons');
  const { testPrepState, dispatch } = useTestPrepState(
    userAccessBadge,
    isTestPrepTemplatesEnabled ?? undefined,
  );
  const { selectedPage } = testPrepState;
  const presetState = userAccessBadge?.organization?.region;
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const [gameAPI, setGameAPI] = useState<GameAPI | undefined | null>(undefined);

  useEffect(() => {
    return () => {
      if (gameAPI) {
        gameAPI.disconnect();
      }
    };
  }, [gameAPI]);

  useEffect(() => {
    if (session.data?.idToken && !gameAPI) {
      // GameAPI should refresh its own tokens by itself.
      const gameAPI = new GameAPI(session.data?.idToken);
      setGameAPI(gameAPI);
    }
  }, [session, gameAPI]);

  const onSelectGeneratorFilterCategory = (categoryId: string) => {
    metrics.logEvent('CreatePage.SelectCategory.Clicked', {
      selectedCategory: categoryId,
      selectedLessonCategory: mainLessonCategory,
      schoolState: presetState,
      tenantSubjects,
    });
    dispatchAndSync({
      type: 'SET_GENERATOR_FILTER_CATEGORY',
      payload: categoryId as string,
    });
  };

  if (session.status === 'loading') {
    return (
      <div>
        <T keyName="Loading..." defaultValue="Loading..." />
      </div>
    );
  }

  if (session.status === 'unauthenticated') {
    return (
      <div>
        <T
          keyName="Please sign in to access this feature."
          defaultValue="Please sign in to access this feature."
        />
      </div>
    );
  }

  if (!gameAPI) {
    return (
      <CenteredContainer>
        <Loader />
      </CenteredContainer>
    );
  }

  return (
    <PageContainer $isCentered={isAgentHistoryEmpty}>
      {isAgentHistoryEmpty && (
        <CuripodAIIconContainer>
          <CuripodAIStarsSvg />
          <T keyName="Curipod AI" defaultValue="Curipod AI" />
        </CuripodAIIconContainer>
      )}
      <SocketProvider gameAPI={gameAPI}>
        <CustomIntercomButton
          icon={
            <IconWrapper height="20px" width="20px">
              <QuestionMarkSvg />
            </IconWrapper>
          }
        />
        <HideNextJsLoader />
        <BackgroundContainer>
          <MagicBackground />
        </BackgroundContainer>
        <BackButtonContainer>
          <>
            {!mainLessonCategory && (
              <Link href={`/${tenantId}/new`} passHref>
                <a>
                  <BackButton
                    onClick={() => {
                      metrics.logEvent('CreatePage.GoHome.Clicked', {
                        prompt: prompt,
                        grade: grade,
                        subject: subject,
                        fileType: uploadedFile?.fileType,
                        currentPage: mainLessonCategory,
                        lessonIntent: lessonIntent?.id,
                        schoolState: presetState,
                      });
                    }}
                    type="primary"
                    icon={
                      <IconWrapper height={'30px'} width={'30px'}>
                        <ArrowLeftSvg style={{ marginRight: '0px' }} />
                      </IconWrapper>
                    }
                    styles={{
                      height: 42,
                      width: 110,
                    }}
                  >
                    <T keyName="Back" defaultValue="Back" />
                  </BackButton>
                </a>
              </Link>
            )}
            {mainLessonCategory === 'test_prep' && (
              <Link href={`/${tenantId}/new`} passHref>
                <a>
                  <BackButton
                    onClick={() => {
                      metrics.logEvent('CreatePage.GoHome.Clicked', {
                        prompt: prompt,
                        grade: grade,
                        subject: subject,
                        fileType: uploadedFile?.fileType,
                        currentPage: mainLessonCategory,
                        lessonIntent: lessonIntent?.id,
                        schoolState: presetState,
                      });
                    }}
                    type="primary"
                    icon={
                      <IconWrapper height={'30px'} width={'30px'}>
                        <ArrowLeftSvg style={{ marginRight: '0px' }} />
                      </IconWrapper>
                    }
                    styles={{
                      height: 42,
                      width: 110,
                    }}
                  >
                    <T keyName="Back" defaultValue="Back" />
                  </BackButton>
                </a>
              </Link>
            )}
            {mainLessonCategory === 'writing_skills' && (
              <Link href={`/${tenantId}/create`} passHref>
                <a>
                  <BackButton
                    onClick={() => {
                      dispatchAndSync({ type: 'RESET_ALL' });
                    }}
                    type="primary"
                    icon={
                      <IconWrapper height={'30px'} width={'30px'}>
                        <ArrowLeftSvg style={{ marginRight: '0px' }} />
                      </IconWrapper>
                    }
                    styles={{
                      height: 42,
                      width: 110,
                    }}
                  >
                    <T keyName="Back" defaultValue="Back" />
                  </BackButton>
                </a>
              </Link>
            )}
            {mainLessonCategory === 'upload' && (
              <Link href={`/${tenantId}/new`} passHref>
                <a>
                  <BackButton
                    type="primary"
                    icon={
                      <IconWrapper height={'30px'} width={'30px'}>
                        <ArrowLeftSvg style={{ marginRight: '0px' }} />
                      </IconWrapper>
                    }
                    styles={{
                      height: 42,
                      width: 110,
                    }}
                  >
                    <T keyName="Back" defaultValue="Back" />
                  </BackButton>
                </a>
              </Link>
            )}
          </>
        </BackButtonContainer>
        {mainLessonCategory === 'test_prep' && lessonCategories['test_prep'].categories && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            <TestPrepTab testPrepState={testPrepState} dispatch={dispatch} />
          </div>
        )}
        {mainLessonCategory === 'writing_skills' && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            {isDesktop ? (
              <SubPageLayout
                pageTitle={t(
                  'Choose your quick write lesson',
                  'Choose your quick write lesson',
                )}
              >
                <QuickWritesContainer>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <CategoryList
                      handleSelectCategory={(categoryId: string) => {
                        onSelectGeneratorFilterCategory(categoryId);
                      }}
                      visibleCategories={writingSkills.categories || []}
                      activeCategoryId={generatorFilterCategory}
                    />
                    {generatorFilterCategory && (
                      <FindCategorizedLessonsTab
                        lessonCategoryId={generatorFilterCategory}
                        selectedCategory={generatorFilterCategory}
                      />
                    )}
                  </div>
                </QuickWritesContainer>
              </SubPageLayout>
            ) : (
              <>
                {!generatorFilterCategory ? (
                  <SubPageLayout pageTitle="Choose a Category">
                    <QuickWritesContainer>
                      <CategoryList
                        handleSelectCategory={(categoryId: string) => {
                          onSelectGeneratorFilterCategory(categoryId);
                        }}
                        visibleCategories={writingSkills.categories || []}
                        activeCategoryId={generatorFilterCategory}
                      />
                    </QuickWritesContainer>
                  </SubPageLayout>
                ) : (
                  <SubPageLayout pageTitle="Quick Writes">
                    <QuickWritesContainer>
                      <FindCategorizedLessonsTab
                        categoryTitle={
                          writingSkills.categories?.find(
                            category => category.id === generatorFilterCategory,
                          )?.title || ''
                        }
                        lessonCategoryId={generatorFilterCategory}
                        selectedCategory={generatorFilterCategory}
                      />
                    </QuickWritesContainer>
                  </SubPageLayout>
                )}
              </>
            )}
          </div>
        )}
        {mainLessonCategory === 'upload' && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            <CurifyTab />
          </div>
        )}

        {mainLessonCategory === 'pick_theme' && (
          <div
            style={{
              zIndex: 1,
              position: 'relative',
              paddingTop: isDesktop ? '80px' : '20px',
              width: '100%',
              paddingBottom: '30px',
            }}
          >
            <ChooseTheme />
          </div>
        )}

        {(!mainLessonCategory || mainLessonCategory === 'full_lesson') && (
          <ObjectiveRefinementWithSocket />
        )}
      </SocketProvider>
    </PageContainer>
  );
};

const ObjectiveRefinement = () => {
  return <ObjectiveRefinementComponent />;
};
export default ObjectiveRefinement;

const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  svg {
    height: 98dvh; // 99 and 100 adds a scroll...
    width: 100dvw;
  }
`;
const BackButton = styled(Button)<{ $iconHeight?: number; $iconWidth?: number }>`
  padding: 10px 20px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.magicFloatingShadow};
  color: ${theme.colors.black};

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight};
    color: ${theme.colors.black};
    box-shadow: ${theme.effects.defaultShadow};
  }

  svg {
    height: ${({ $iconHeight }) => $iconHeight ?? '16'}px;
    width: ${({ $iconWidth }) => $iconWidth ?? '16'}px;
  }
`;
const BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  margin-left: 20px;
  width: fit-content;
  z-index: 3;
`;
const PageContainer = styled.div<{ $isCentered?: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  background: #ffc698;
  background: linear-gradient(
    180deg,
    rgb(255, 245, 238) 0%,
    rgba(255, 198, 152, 0.8) 25%,
    rgba(255, 198, 152, 0.8) 50%,
    rgb(255, 245, 238) 100%
  );
`;
const CuripodAIIconContainer = styled.div`
  position: absolute;
  cursor: default;
  top: 20px;
  right: 20px;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-direction: column;
  font-size: 14px;
  font-weight: 600;
  width: fit-content;
  color: ${theme.colors.orange};
  svg {
    width: 100%;
    height: auto;
  }
`;

const QuickWritesContainer = styled.div`
  margin: 0 auto;
  width: 80%;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }
`;
